import { SxProps, Theme } from '@mui/material'
import {
  ACCENT_BLUE,
  SLATE_100,
  SLATE_200,
  SLATE_300,
  SLATE_400,
  SLATE_50,
  SLATE_500,
  SLATE_900,
  WHITE,
} from '../config/constants'

/**
 * Estilos de `LayoutMapPage` y de los componentes que viven en ese archivo
 * (`BrandSelectInput`, `DataFreshness`, `OccupancyPanel`).
 *
 * Cada componente de `components/` tiene su propio archivo de estilos; lo que
 * comparten la grilla y la vista de pasillo vive en `shared.styles`.
 */

/** Alto del input de marca; iguala al del resto de los filtros. */
const FILTER_INPUT_HEIGHT = '44px'

export const layoutMapStyles = {
  // ── Filtros ───────────────────────────────────────────────────────────────

  brandSelect: {
    '& .MuiInputBase-root': { height: FILTER_INPUT_HEIGHT },
  } as SxProps<Theme>,

  // ── Carga y error ─────────────────────────────────────────────────────────

  loadingWrapper: {
    display: 'flex',
    justifyContent: 'center',
    py: 6,
  } as SxProps<Theme>,

  /** Spinner propio: un anillo con un lado transparente girando sobre sí mismo. */
  spinner: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    border: '3px solid',
    borderColor: 'primary.main',
    borderTopColor: 'transparent',
    animation: 'spin 0.8s linear infinite',
    '@keyframes spin': { to: { transform: 'rotate(360deg)' } },
  } as SxProps<Theme>,

  errorText: { py: 2 } as SxProps<Theme>,

  // ── Estado vacío (sin almacén elegido) ────────────────────────────────────

  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1.5,
    py: 8,
    px: 2,
  } as SxProps<Theme>,

  emptyStateIconCircle: {
    width: 72,
    height: 72,
    borderRadius: '50%',
    bgcolor: SLATE_100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  } as SxProps<Theme>,

  emptyStateIcon: { fontSize: 38, color: SLATE_400 } as SxProps<Theme>,

  emptyStateTitle: { mt: 0.5 } as SxProps<Theme>,

  emptyStateText: {
    maxWidth: 340,
    textAlign: 'center',
    lineHeight: 1.6,
  } as SxProps<Theme>,

  // ── Banda de controles: almacén · modo de vista · métricas ────────────────

  controlsBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    columnGap: 1.5,
    rowGap: 1,
    mb: 1,
    pb: 1,
    borderBottom: `1px solid ${SLATE_200}`,
  } as SxProps<Theme>,

  controlsGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    minWidth: 0,
  } as SxProps<Theme>,

  warehouseIcon: {
    fontSize: 18,
    color: 'text.secondary',
    flexShrink: 0,
  } as SxProps<Theme>,

  /** El nombre se trunca antes que empujar los botones de modo fuera de la fila. */
  warehouseName: {
    color: SLATE_900,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: { xs: 120, md: 180, xl: 260 },
  } as SxProps<Theme>,

  verticalDivider: {
    width: '1px',
    height: 22,
    bgcolor: SLATE_200,
    mx: 1,
    flexShrink: 0,
  } as SxProps<Theme>,

  /** Se oculta en pantallas chicas: los botones ya se explican solos. */
  modeLabel: {
    display: { xs: 'none', lg: 'block' },
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    mr: 0.5,
  } as SxProps<Theme>,

  /** Sin `minWidth` en pantallas chicas para que los tres modos entren en la fila. */
  modeButton: (isActive: boolean): SxProps<Theme> => ({
    textTransform: 'none',
    minWidth: { xs: 0, lg: 72 },
    px: { xs: 1, lg: 1.5 },
    flexShrink: 0,
    fontWeight: isActive ? 700 : 400,
  }),

  metricsGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    ml: 'auto',
  } as SxProps<Theme>,

  // ── Leyenda y nota de resaltado ───────────────────────────────────────────

  legendRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 2,
    mb: 1,
  } as SxProps<Theme>,

  highlightNote: {
    display: 'block',
    color: SLATE_500,
    mb: 0.75,
  } as SxProps<Theme>,

  // ── Antigüedad del dato y botón Recalcular ────────────────────────────────

  freshnessRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    flexShrink: 0,
  } as SxProps<Theme>,

  freshnessCaption: {
    fontStyle: 'italic',
    whiteSpace: 'nowrap',
  } as SxProps<Theme>,

  /**
   * El `span` que envuelve al botón: un elemento deshabilitado no emite eventos
   * de mouse, así que sin envoltorio el Tooltip no aparece durante el cooldown
   * —justo cuando el usuario más quiere saber qué pasa—. Patrón documentado por MUI.
   */
  tooltipWrapper: { display: 'inline-flex' } as SxProps<Theme>,

  refreshIcon: { fontSize: 16 } as SxProps<Theme>,

  refreshButton: {
    textTransform: 'none',
    whiteSpace: 'nowrap',
    height: 30,
    minWidth: 0,
    borderRadius: '12px',
    px: 1.25,
    fontSize: '0.75rem',
    borderColor: SLATE_300,
    '&:hover': {
      backgroundColor: ACCENT_BLUE,
      borderColor: ACCENT_BLUE,
      color: WHITE,
    },
  } as SxProps<Theme>,

  // ── Panel de métricas de ocupación ────────────────────────────────────────

  occupancyPanel: {
    display: 'flex',
    alignItems: 'stretch',
    bgcolor: SLATE_50,
    border: `1px solid ${SLATE_200}`,
    borderRadius: 2,
    overflow: 'hidden',
  } as SxProps<Theme>,

  occupancyCell: {
    px: 1.5,
    py: 0.75,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  } as SxProps<Theme>,

  occupancyDivider: {
    width: '1px',
    alignSelf: 'stretch',
    bgcolor: SLATE_200,
  } as SxProps<Theme>,

  statLabel: {
    fontSize: '0.62rem',
    fontWeight: 700,
    color: SLATE_400,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    lineHeight: 1,
    mb: 0.5,
  } as SxProps<Theme>,

  statValue: {
    fontWeight: 700,
    fontSize: '1rem',
    color: SLATE_900,
    lineHeight: 1,
    alignSelf: 'center',
  } as SxProps<Theme>,
}
